import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { NAV_GROUPS } from './nav-items';
import type { NavItem } from './nav-items';
import { DrawerHeader } from './DrawerHeader';
import { DrawerProfile } from './DrawerProfile';
import { ExternalToolLinkRow } from './ExternalToolLinkRow';
import { useSession } from '@/auth/hooks';
import { hasAnyRole, ROUTE_ROLES, type RouteKey } from '@/auth/rbac';
import { usePagePermissionsStore } from '@/auth/page-permissions';
import { configuredModerationToolLinks } from '@/lib/moderationToolLinks';
import { useCoopModerationMetrics } from '@/features/moderation/api';
import { cn } from '@/lib/utils';

function NavBadge({
  count,
  tone = 'amber',
}: {
  count: number;
  tone?: 'amber' | 'violet';
}) {
  if (count <= 0) return null;
  const label = count > 99 ? '99+' : String(count);
  return (
    <span
      className={cn(
        'ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white',
        tone === 'violet' ? 'bg-violet-500/90' : 'bg-amber-500/90',
      )}
    >
      {label}
    </span>
  );
}

function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (pathname === item.path || pathname.startsWith(`${item.path}/`)) return true;
  return (item.children ?? []).some(
    (child) => pathname === child.path || pathname.startsWith(`${child.path}/`),
  );
}

function SidebarNavItem({
  item,
  humanReviewPending,
  frontierQueue,
  visibleChildren,
}: {
  item: NavItem;
  humanReviewPending: number;
  frontierQueue: number;
  visibleChildren: NonNullable<NavItem['children']>;
}) {
  const location = useLocation();
  const hasChildren = visibleChildren.length > 0;

  // Auto-expand if current path matches this item or any visible child
  // (children may not share the parent path prefix, e.g. /admins vs /system/permissions).
  const isParentActive = isNavItemActive(item, location.pathname);
  const [expanded, setExpanded] = useState(isParentActive);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.path}
        end={item.key === 'dashboard'}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-4 rounded-lg px-5 py-3 text-sm text-sidebar-foreground',
            'hover:bg-sidebar-accent',
            isActive && 'bg-sidebar-accent',
          )
        }
      >
        <item.icon className="size-5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
      </NavLink>
    );
  }

  // When siblings map to different page keys (alsoKeys), land on the first
  // visible child so a partial grant does not 403 on the parent path.
  const landingPath =
    item.alsoKeys?.length && visibleChildren[0]
      ? visibleChildren[0].path
      : item.path;

  const parentBadge =
    item.key === 'content' && humanReviewPending > 0 ? humanReviewPending : 0;

  return (
    <div>
      {/* Parent item — navigates AND toggles submenu */}
      <div className="flex items-center">
        <NavLink
          to={landingPath}
          end
          className={({ isActive }) =>
            cn(
              'flex flex-1 items-center gap-4 rounded-lg rounded-r-none px-5 py-3 text-sm text-sidebar-foreground',
              'hover:bg-sidebar-accent',
              (isActive || isParentActive) && 'bg-sidebar-accent',
            )
          }
        >
          <item.icon className="size-5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          <NavBadge count={parentBadge} />
        </NavLink>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            'flex h-11 w-9 items-center justify-center rounded-lg rounded-l-none text-sidebar-foreground',
            'hover:bg-sidebar-accent',
            isParentActive && 'bg-sidebar-accent',
          )}
          aria-label={expanded ? 'Collapse submenu' : 'Expand submenu'}
        >
          <ChevronDown
            className={cn('size-4 transition-transform', expanded ? '' : '-rotate-90')}
          />
        </button>
      </div>

      {/* Children */}
      {expanded && (
        <div className="ml-9 mt-0.5 space-y-0.5 border-l border-sidebar-border/30 pl-4">
          {visibleChildren.map((child) => {
            const childBadge =
              child.badgeKey === 'humanReview'
                ? humanReviewPending
                : child.badgeKey === 'frontierReview'
                  ? frontierQueue
                  : 0;
            const badgeTone = child.badgeKey === 'frontierReview' ? 'violet' : 'amber';
            return (
              <NavLink
                key={child.path}
                to={child.path}
                end={child.path === item.path}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm text-sidebar-foreground',
                    'hover:bg-sidebar-accent',
                    isActive && 'bg-sidebar-accent font-medium',
                  )
                }
              >
                <span className="min-w-0 flex-1 truncate">{child.label}</span>
                <NavBadge count={childBadge} tone={badgeTone} />
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function SidebarDrawer({ className }: { className?: string }) {
  const { roles } = useSession();
  const pageKeys = usePagePermissionsStore((s) => s.pageKeys);
  const permsReady = usePagePermissionsStore((s) => s.status === 'ready' || s.status === 'error');
  const externalLinks = configuredModerationToolLinks();

  const canSeeKey = (key: RouteKey): boolean =>
    permsReady ? pageKeys.has(key) : hasAnyRole(roles, ROUTE_ROLES[key]);

  const canSeeItem = (item: NavItem): boolean => {
    if (canSeeKey(item.key)) return true;
    return (item.alsoKeys ?? []).some((k) => canSeeKey(k));
  };

  const visibleChildrenFor = (item: NavItem): NonNullable<NavItem['children']> => {
    const children = item.children ?? [];
    return children.filter((child) => (child.pageKey ? canSeeKey(child.pageKey) : true));
  };

  // Prefer effective page keys from System & Permissions; fall back to ROUTE_ROLES seed.
  const groups = NAV_GROUPS.map((g) =>
    g
      .filter(canSeeItem)
      .map((item) => ({ item, children: visibleChildrenFor(item) }))
      // Drop a grouped parent if every pageKey-gated child was filtered out.
      .filter(({ item, children }) => !item.children?.length || children.length > 0),
  ).filter((g) => g.length > 0);

  const canSeeContent = canSeeKey('content') || canSeeKey('moderation');
  const metrics = useCoopModerationMetrics({
    enabled: canSeeContent,
    refetchInterval: canSeeContent ? 60_000 : false,
  });
  const humanReviewPending = canSeeContent ? (metrics.data?.inHumanQueue ?? 0) : 0;
  const frontierQueue = canSeeContent
    ? (metrics.data?.frontierQueue ?? metrics.data?.gpuEscalationsToday ?? 0)
    : 0;

  return (
    <nav className={cn('flex h-full min-h-0 flex-col overflow-hidden py-4', className)}>
      <DrawerHeader />
      <div className="mt-5 min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-y-contain px-3">
        {groups.map((group, i) => (
          <div key={i}>
            {group.map(({ item, children }) => (
              <SidebarNavItem
                key={item.key}
                item={item}
                visibleChildren={children}
                humanReviewPending={humanReviewPending}
                frontierQueue={frontierQueue}
              />
            ))}
            {i < groups.length - 1 && (
              <div className="my-3 border-t border-sidebar-border/30" />
            )}
          </div>
        ))}

        {externalLinks.length > 0 && (
          <>
            <div className="my-3 border-t border-sidebar-border/30" />
            {externalLinks.map((link) => (
              <ExternalToolLinkRow key={link.url} link={link} />
            ))}
          </>
        )}
      </div>
      <DrawerProfile />
    </nav>
  );
}
